import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  type NestedListRowData,
  NestedListRowGroup,
  TabContent,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodMessage, SodReloader } from '../../../../../shared/components';
import {
  GLOBAL_ALERT_BANNER_TYPES,
  SEARCH_TEXT_LENGTH,
  VIOLATIONS_DETAILS_TABS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import {
  detailsActions,
  globalActions,
} from '../../../../../shared/store/actions';
import {
  type TrackedViolationAssignedEntitlements,
  type TrackedViolationFunctions,
  type ViolationConflictTrackers,
  type ViolationFunctions,
} from '../../../data-layer/detailsTypes';
import {
  useConflictApiEndpoints,
  useFiltersInitialization,
} from '../../../hooks';
import { closeAllNesting } from '../../../utils';

import ViolationsTabDetailsConflictsHeader from './ViolationsTabDetailsConflictsHeader';
import {
  ViolationsTabDetailsConflictsLoader,
  ViolationsTabDetailsConflictsNestedLoader,
} from './ViolationsTabDetailsConflictsLoader';
import {
  ConflictAfterContent,
  ConflictCenterContent,
  ConflictLeadingContent,
  ConflictTrailingContent,
} from './ViolationsTabDetailsConflictsShared';

import './ViolationsTabDetailsConflicts.css';

interface ViolationsTabDetailsConflictsProps {
  activeTab: string;
  className?: string;
}

/**
 * ViolationsTabDetailsConflicts
 *
 * TODO:
 * - Conflicting Job Functions localized and dynamically rendered number
 * - Fix search expand transition jumping
 * - search should work
 * - Find out if the view controls should be position sticky (fixed)
 * - Find out if it should be and how to make the selected row sticky?
 * - Occurrences X of X needs to be dynamic
 * - Trust Score Chip should be dynamic
 * - Row Icon should be dynamic, in this file as: 'Remove the fallback and move to getSwitch function for Icons'
 */
function ViolationsTabDetailsConflicts({
  activeTab,
  className = '',
}: ViolationsTabDetailsConflictsProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const assignedEntitlementTracker = useRef<Record<number, number>>({});
  const [rowsData, setRowsData] = useState<NestedListRowData[]>([]);

  const {
    queryParam,
    sidepanel: { isSidePanelVisible },
    violationsTab: {
      conflicts: {
        selectedRowData,
        controls: { activeFilters, searchKey, expandKey },
      },
    },
  } = useTypedSelector((state) => state.aag.details);

  // Filter hook
  useFiltersInitialization();

  // API hooks
  const {
    handleFetchViolationFunctions,
    handleFetchAssignedEntitlements,
    handleFetchViolatingEntitlements,
    states: { isFunctionError, isFunctionLoading },
  } = useConflictApiEndpoints(activeFilters, searchKey);

  const sodKey = useMemo(() => queryParam, [queryParam]);
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );
  // Endpoints to pass in Entitlment Filters
  const endpointNames = useMemo(
    () =>
      rowsData.map(
        (fn) => (fn as { data: ViolationFunctions }).data.endpointName
      ),
    [rowsData]
  );

  // Handle violating entitlements
  const triggerFetchViolatingEntitlements = useCallback(
    (
      violationId: string,
      functionId: string,
      assignedEntitlementId: string,
      searchAfter: string[] | null
    ) => {
      const isNotValidApiCall = Boolean(
        activeTab !== VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS ||
          !violationId ||
          !functionId ||
          !assignedEntitlementId
      );

      if (isNotValidApiCall) {
        return;
      }

      setRowsData((prev) =>
        prev.map((fn) => {
          if (fn.id === Number(functionId)) {
            return {
              ...fn,
              childRows: fn.childRows?.map((ent) => {
                const uniqueId = Number(`${fn.id}${assignedEntitlementId}`);
                const { childrenCount } = (
                  ent as { data: { tracker: ViolationConflictTrackers } }
                ).data.tracker;

                if (ent.id === uniqueId) {
                  return {
                    ...ent,
                    afterContent: ent.afterContent && (
                      <ConflictAfterContent isDisabled />
                    ),
                    childRows:
                      childrenCount > 0
                        ? [
                            ...(ent.childRows ?? []),
                            ViolationsTabDetailsConflictsNestedLoader(ent.id),
                          ]
                        : ent.childRows,
                  };
                }

                return ent;
              }),
            };
          }

          return fn;
        })
      );

      handleFetchViolatingEntitlements(
        violationId,
        functionId,
        assignedEntitlementId,
        searchAfter
      )
        .then(({ data: response, currentAfterKey }) => {
          const isValidNextCall = Array.isArray(currentAfterKey);

          setRowsData((prev) =>
            prev.map((fn) => {
              if (fn.id === Number(functionId)) {
                return {
                  ...fn,
                  childRows: fn.childRows?.map((ent) => {
                    const prevUniqueId = Number(
                      `${fn.id}${assignedEntitlementId}`
                    );

                    // Matching Assigned Entitlement
                    if (ent.id === prevUniqueId) {
                      const { data: typedData } = ent as {
                        data: TrackedViolationAssignedEntitlements;
                      };
                      const {
                        childrenCount: currentChildrenCount,
                        level: currentLevel,
                      } = typedData.tracker;
                      const newChildrenCount =
                        currentChildrenCount + response.length;

                      return {
                        ...ent,
                        data: {
                          ...typedData,
                          tracker: {
                            ...typedData.tracker,
                            childrenCount: newChildrenCount,
                          },
                        },
                        afterContent: isValidNextCall ? (
                          <ConflictAfterContent
                            onClick={() => {
                              triggerFetchViolatingEntitlements(
                                violationId,
                                functionId,
                                assignedEntitlementId,
                                currentAfterKey
                              );
                            }}
                          />
                        ) : undefined,
                        handleExpandToggler: undefined,
                        // rendering third level violating entitlements/objects
                        childRows: [
                          ...(ent.childRows?.slice(0, currentChildrenCount) ??
                            []),
                          ...response.map((violatingEnt, index) => {
                            // CASE 1: showObjectFieldLabel is true and object is null
                            //         consider as `object`
                            // CASE 2: showObjectFieldLabel is true and object is not null
                            //         consider as `violating entitlement` with `objects`
                            // CASE 3: showObjectFieldLabel is false
                            //         consider as only `violating entitlement`
                            const isEntitlementObject = Boolean(
                              violatingEnt.showObjectFieldLabel &&
                                violatingEnt.object === null
                            );
                            const uniqueId = Number(
                              `${fn.id}${typedData.tracker.id}${
                                isEntitlementObject
                                  ? violatingEnt.entitlementKey
                                  : currentChildrenCount + index
                              }`
                            );

                            // uniqueId are important to track
                            return {
                              id: uniqueId,
                              data: {
                                ...violatingEnt,
                                id: uniqueId,
                                violationId,
                                assignedEntitlementId,
                                tracker: {
                                  id: isEntitlementObject
                                    ? violatingEnt.entitlementKey
                                    : currentChildrenCount + index,
                                  level: currentLevel + 1,
                                  childrenCount: 0,
                                  type: isEntitlementObject
                                    ? 'Object'
                                    : 'Violating Entilement',
                                },
                              },
                              isCheckboxDisabled: true,
                              isDisabled: isEntitlementObject,
                              leadingComp: (
                                <ConflictLeadingContent
                                  level={currentLevel + 1}
                                  isObject={isEntitlementObject}
                                />
                              ),
                              centerContent: (
                                <ConflictCenterContent
                                  level={currentLevel + 1}
                                  vioRow={violatingEnt}
                                  isObject={isEntitlementObject}
                                />
                              ),
                              trailingContent: (
                                <ConflictTrailingContent
                                  level={currentLevel + 1}
                                  vioRow={violatingEnt}
                                  isObject={isEntitlementObject}
                                />
                              ),
                              childRows:
                                !isEntitlementObject && violatingEnt.object
                                  ? [
                                      ...violatingEnt.object.map(
                                        (violatingObject, objectIndex) => ({
                                          id: `${uniqueId}${objectIndex}`,
                                          data: {
                                            ...violatingObject,
                                            id: `${uniqueId}${objectIndex}`,
                                            tracker: {
                                              id: `${uniqueId}${objectIndex}`,
                                              level: currentLevel + 2,
                                              childrenCount: 0,
                                              type: 'Object',
                                            },
                                          },
                                          isCheckboxDisabled: true,
                                          isDisabled: true,
                                          leadingComp: (
                                            <ConflictLeadingContent
                                              level={currentLevel + 2}
                                              isObject
                                            />
                                          ),
                                          centerContent: (
                                            <ConflictCenterContent
                                              level={currentLevel + 2}
                                              vioObj={violatingObject}
                                              isObject
                                            />
                                          ),
                                          trailingContent: (
                                            <ConflictTrailingContent
                                              level={currentLevel + 2}
                                              isObject
                                            />
                                          ),
                                        })
                                      ),
                                    ]
                                  : undefined,
                            };
                          }),
                        ],
                      };
                    }

                    return ent;
                  }),
                };
              }

              return fn;
            })
          );
        })
        .catch((error) => {
          Logger.log('error', error);

          dispatch(
            globalActions.setAlertBanner({
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: l('Action Failed'),
              description: l(
                'Something went wrong while loading the data. Please reload or check back later.'
              ),
              isOpen: true,
            })
          );

          // Back to initial state
          setRowsData((prev) =>
            prev.map((fn) => {
              if (fn.id === Number(functionId)) {
                return {
                  ...fn,
                  childRows: fn.childRows?.map((ent) => {
                    const uniqueId = Number(`${fn.id}${assignedEntitlementId}`);
                    const { childrenCount } = (
                      ent as { data: { tracker: ViolationConflictTrackers } }
                    ).data.tracker;

                    if (ent.id === uniqueId) {
                      return {
                        ...ent,
                        afterContent: fn.afterContent && (
                          <ConflictAfterContent
                            onClick={() => {
                              triggerFetchViolatingEntitlements(
                                violationId,
                                functionId,
                                assignedEntitlementId,
                                searchAfter
                              );
                            }}
                            isRetry
                          />
                        ),
                        childRows:
                          childrenCount > 0
                            ? [...(fn.childRows?.slice(0, childrenCount) ?? [])]
                            : [
                                ViolationsTabDetailsConflictsNestedLoader(
                                  ent.id
                                ),
                              ],
                      };
                    }

                    return ent;
                  }),
                };
              }

              return fn;
            })
          );
        });
    },
    [l, dispatch, activeTab, handleFetchViolatingEntitlements]
  );

  // Handle Assigned Entitlement calls
  const triggerFetchAssignedEntitlements = useCallback(
    (
      violationId: string,
      functionId: string,
      afterKey: string | null,
      isOnExpand = false
    ) => {
      const isNotValidApiCall = Boolean(
        activeTab !== VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS ||
          !violationId ||
          !functionId
      );

      if (isNotValidApiCall) {
        return;
      }

      // Prepare for loading state
      setRowsData((prev) =>
        prev.map((fn) => {
          if (fn.id === Number(functionId)) {
            const { childrenCount } = (
              fn as { data: { tracker: ViolationConflictTrackers } }
            ).data.tracker;

            return {
              ...fn,
              afterContent: fn.afterContent && (
                <ConflictAfterContent isDisabled />
              ),
              childRows:
                childrenCount > 0
                  ? [
                      ...(fn.childRows ?? []),
                      ViolationsTabDetailsConflictsNestedLoader(fn.id),
                    ]
                  : fn.childRows,
            };
          }

          return fn;
        })
      );

      handleFetchAssignedEntitlements(
        violationId,
        functionId,
        afterKey,
        isOnExpand
      )
        .then(({ data: response, currentAfterKey }) => {
          const isValidNextCall = Boolean(typeof currentAfterKey === 'string');

          setRowsData((prev) =>
            prev.map((fn) => {
              if (fn.id === Number(functionId)) {
                const { data: typedData } = fn as {
                  data: TrackedViolationFunctions;
                };
                const {
                  childrenCount: currentChildrenCount,
                  level: currentLevel,
                } = typedData.tracker;
                const newChildrenCount = currentChildrenCount + response.length;

                return {
                  ...fn,
                  data: {
                    ...typedData,
                    tracker: {
                      ...typedData.tracker,
                      childrenCount: newChildrenCount,
                    },
                  },
                  afterContent: isValidNextCall ? (
                    <ConflictAfterContent
                      onClick={() => {
                        triggerFetchAssignedEntitlements(
                          violationId,
                          functionId,
                          currentAfterKey
                        );
                      }}
                    />
                  ) : undefined,
                  handleExpandToggler: undefined,
                  childRows: [
                    ...(fn.childRows?.slice(0, currentChildrenCount) ?? []),
                    ...response.map((ent) => {
                      const uniqueId = Number(`${fn.id}${ent.entitlementKey}`);

                      const appearanceCount =
                        assignedEntitlementTracker.current[
                          Number(ent.entitlementKey)
                        ];

                      if (appearanceCount) {
                        assignedEntitlementTracker.current[
                          Number(ent.entitlementKey)
                        ] += 1;
                      } else {
                        assignedEntitlementTracker.current[
                          Number(ent.entitlementKey)
                        ] = 1;
                      }

                      // uniqueId are important to track
                      return {
                        id: uniqueId,
                        data: {
                          ...ent,
                          id: uniqueId,
                          tracker: {
                            id: Number(ent.entitlementKey),
                            level: currentLevel + 1,
                            childrenCount: 0,
                            type: 'Assigned Entilement',
                          },
                        },
                        isCheckboxDisabled: true,
                        leadingComp: (
                          <ConflictLeadingContent level={currentLevel + 1} />
                        ),
                        centerContent: (
                          <ConflictCenterContent
                            level={currentLevel + 1}
                            entRow={ent}
                          />
                        ),
                        trailingContent: (
                          <ConflictTrailingContent
                            level={currentLevel + 1}
                            entRow={ent}
                            appearanceCount={appearanceCount}
                          />
                        ),
                        handleExpandToggler: () =>
                          triggerFetchViolatingEntitlements(
                            violationId,
                            functionId,
                            String(ent.entitlementKey),
                            null
                          ),
                        childRows: [
                          ViolationsTabDetailsConflictsNestedLoader(uniqueId),
                        ],
                      };
                    }),
                  ],
                };
              }

              return fn;
            })
          );
        })
        .catch((error) => {
          Logger.log('error', error);

          dispatch(
            globalActions.setAlertBanner({
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: l('Action Failed'),
              description: l(
                'Something went wrong while loading the data. Please reload or check back later.'
              ),
              isOpen: true,
            })
          );

          // Back to initial state
          setRowsData((prev) =>
            prev.map((fn) => {
              if (fn.id === Number(functionId)) {
                const { childrenCount } = (
                  fn as { data: { tracker: ViolationConflictTrackers } }
                ).data.tracker;

                return {
                  ...fn,
                  afterContent: fn.afterContent && (
                    <ConflictAfterContent
                      onClick={() => {
                        triggerFetchAssignedEntitlements(
                          violationId,
                          functionId,
                          afterKey
                        );
                      }}
                      isRetry
                    />
                  ),
                  childRows:
                    childrenCount > 0
                      ? [...(fn.childRows?.slice(0, childrenCount) ?? [])]
                      : [ViolationsTabDetailsConflictsNestedLoader(fn.id)],
                };
              }

              return fn;
            })
          );
        });
    },
    [
      l,
      dispatch,
      activeTab,
      handleFetchAssignedEntitlements,
      triggerFetchViolatingEntitlements,
    ]
  );

  // Handle Function calls
  const triggerFetchViolationFunctions = useCallback(
    (violationId: string | undefined) => {
      // Check for the valid api call
      const isValidApiCall = Boolean(
        activeTab === VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS
      );

      if (!violationId || !isValidApiCall) {
        return;
      }

      handleFetchViolationFunctions(violationId)
        .then((response) => {
          const typedResponse = response as ViolationFunctions[];

          setRowsData(
            typedResponse.map((fn, index) => {
              const currentLevel = 0;

              return {
                id: fn.id,
                data: {
                  ...fn,
                  tracker: {
                    id: fn.id,
                    level: currentLevel,
                    childrenCount: 0,
                    type: 'Function',
                  },
                },
                isCheckboxDisabled: true,
                leadingComp: <ConflictLeadingContent level={currentLevel} />,
                centerContent: (
                  <ConflictCenterContent level={currentLevel} fnRow={fn} />
                ),
                trailingContent: fn.assignedEntitlementsCount ? (
                  <ConflictTrailingContent level={currentLevel} fnRow={fn} />
                ) : null,
                // Call child APIs when row is expanded
                handleExpandToggler: (row: NestedListRowData) =>
                  triggerFetchAssignedEntitlements(
                    violationId,
                    String(row.id),
                    null
                  ),
                // Initial child will be laoder only
                childRows: fn.assignedEntitlementsCount
                  ? [ViolationsTabDetailsConflictsNestedLoader(index)]
                  : undefined,
              };
            })
          );
        })
        .catch((error) => {
          Logger.log('error', error);
        });
    },
    [activeTab, handleFetchViolationFunctions, triggerFetchAssignedEntitlements]
  );

  // Trigger initial calls or when filters/search applied
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // Dispatching neccessary actions
      dispatch(detailsActions.setSidePanelClose());

      // Clearing rows
      assignedEntitlementTracker.current = {};
      setRowsData([]);
      triggerFetchViolationFunctions(sodKey);
    }, 20);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sodKey, searchKey, activeFilters, activeTab]);

  // Expand logic
  useEffect(() => {
    if (expandKey !== null && sodKey) {
      if (expandKey === '-1') {
        setRowsData((prevRowsData) => closeAllNesting(prevRowsData));
        dispatch(detailsActions.setViolationsTabExpandKey(null));
      }

      if (expandKey === '1') {
        const fnIdsToTrigger = rowsData.map((fn) => ({
          id: fn.id,
          expectedChildrenCount: (
            fn.data as { assignedEntitlementsCount: number | null }
          ).assignedEntitlementsCount,
          currentChildrenCount: (
            fn.data as { tracker: ViolationConflictTrackers }
          ).tracker.childrenCount,
        }));

        setRowsData((prevRowsData) =>
          prevRowsData.map((fn) => ({
            ...fn,
            isNestedRowExpanded: true,
          }))
        );

        // Trigger api call one by one
        fnIdsToTrigger.forEach((triggerObject) => {
          if (
            triggerObject.expectedChildrenCount &&
            triggerObject.currentChildrenCount === 0 &&
            triggerObject.expectedChildrenCount > 0
          ) {
            triggerFetchAssignedEntitlements(
              String(sodKey),
              String(triggerObject.id),
              null,
              true
            );
          }
        });
        dispatch(detailsActions.setViolationsTabExpandKey(null));
      }
    }
  }, [dispatch, expandKey, rowsData, sodKey, triggerFetchAssignedEntitlements]);

  // Sidepanel calls
  const handleConflictRow = useCallback(
    (row: TrackedViolationFunctions) => {
      batch(() => {
        dispatch(detailsActions.setViolationsTabConflictRow(row));

        if (!isSidePanelVisible) {
          dispatch(detailsActions.setIsSidePanelVisible(true));
        }
      });
    },
    [dispatch, isSidePanelVisible]
  );

  const conflictTabContentClasses = cx(
    'Sod-ViolationsTabDetailsConflicts',
    className
  );

  // Body for conflict tab
  const renderBody = useCallback(() => {
    if (isFunctionLoading) {
      return <ViolationsTabDetailsConflictsLoader />;
    }

    if (isFunctionError) {
      return (
        <SodReloader
          triggerRefresh={() => {
            if (sodKey) {
              triggerFetchViolationFunctions(sodKey);
            }
          }}
        />
      );
    }

    if (rowsData.length === 0) {
      return (
        <SodMessage
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          handleFilterClearBtn={() => {
            dispatch(detailsActions.setViolationsTabActiveFilters([]));
          }}
        />
      );
    }

    const scrollWrapperClasses = cx(
      'Sod-ViolationsTabDetailsConflicts-nestedListGroupScrollArea',
      isSidePanelVisible &&
        'Sod-ViolationsTabDetailsConflicts-nestedListGroupScrollArea--isSidePanelVisible'
    );

    return (
      <Box tag='div' className={scrollWrapperClasses}>
        <NestedListRowGroup
          rowsData={rowsData}
          setRowsData={setRowsData}
          selectedRowData={selectedRowData}
          setSelectedRowData={handleConflictRow}
          tallestRowHeight={5.5}
          afterContentHeight={2.5}
        />
      </Box>
    );
  }, [
    dispatch,
    sodKey,
    rowsData,
    selectedRowData,
    isSearchActive,
    isFilterActive,
    isFunctionError,
    isFunctionLoading,
    isSidePanelVisible,
    handleConflictRow,
    triggerFetchViolationFunctions,
  ]);

  return (
    <TabContent
      value={activeTab}
      tabValue={VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS}
      className={conflictTabContentClasses}>
      <ViolationsTabDetailsConflictsHeader
        endpointNames={endpointNames}
        isDisabled={false}
      />
      {renderBody()}
    </TabContent>
  );
}

ViolationsTabDetailsConflicts.defaultProps = {
  className: null,
};

export default memo(ViolationsTabDetailsConflicts);
